import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Checkbox,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import React, { useMemo, useRef, useState } from "react";
import { showErrorToast, showSuccessToast } from "../../../utils";
import {
  dimensionAdminKeys,
  useRenameDimensionItem,
  useAddChildDimensionItem,
  useAddDimensionItem,
  useSetDimensionItemDefaultAssignee,
} from "../../../hooks/query/dimensions/dimensionAdmin";
import { useGetOrganizationUsers } from "../../../hooks/query";
import { useQueryClient } from "react-query";
import useAirUserRoles from "../../../utils/AuthUtils";
import { sortUsersByEmail } from "../../../utils/userSort";

function EditDimensionItemModal({ isOpen, onClose, item, parentItem, childDimensionName, rootDimension, onSuccess }) {
  const toast = useToast();
  const queryClient = useQueryClient();
  // ORG_ADMIN can only edit the default-assignee dropdown; everything else is locked.
  // Add new structural fields BEHIND `isESGAdmin` if they should not be ORG_ADMIN-editable.
  const { isESGAdmin } = useAirUserRoles();
  const isEditing = !!item;
  const isAddingRoot = !!rootDimension && !parentItem;

  const [name, setName] = useState(item?.name || "");
  const initialAssigneeId = item?.defaultAssigneeId != null ? String(item.defaultAssigneeId) : "";
  const [defaultAssigneeId, setDefaultAssigneeId] = useState(initialAssigneeId);
  const [applyToDescendants, setApplyToDescendants] = useState(false);

  // Confirmation dialog for overwriting descendants that already have an assignee.
  const [confirmState, setConfirmState] = useState({ open: false, conflicts: [] });
  const cancelConfirmRef = useRef(null);

  const { data: users, isLoading: isUsersLoading } = useGetOrganizationUsers({ cacheTime: 60_000 });

  // Walk the tree-node prop to collect every descendant's id. Note: items
  // marked `childrenOmitted` (DAG dedupe — shared items appear under a
  // different parent in the response) won't be traversed here, so a rare
  // shared subtree can be missed. Acceptable trade-off for the local-only
  // shim; revisit with the backend version.
  const collectDescendants = (node) => {
    const out = [];
    if (!node || !Array.isArray(node.children)) return out;
    const visit = (n) => {
      if (!n) return;
      out.push(n);
      if (Array.isArray(n.children)) n.children.forEach(visit);
    };
    node.children.forEach(visit);
    return out;
  };

  const descendants = useMemo(() => (item ? collectDescendants(item) : []), [item]);
  const hasDescendants = descendants.length > 0;

  const { mutateAsync: addChildItemAsync, isLoading: isAddingChild } = useAddChildDimensionItem({
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
  });

  const { mutateAsync: addRootItemAsync, isLoading: isAddingRootItem } = useAddDimensionItem({
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
  });

  const { mutateAsync: renameItemAsync, isLoading: isRenaming } = useRenameDimensionItem({
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
  });

  const { mutateAsync: setDefaultAssigneeAsync, isLoading: isSettingAssignee } = useSetDimensionItemDefaultAssignee({
    onError: (err) => {
      showErrorToast(toast, err.response?.data?.parameters?.message ?? err.message);
    },
  });

  const findNewItemId = (createdDimension, itemName) => {
    if (!createdDimension?.items) return null;
    const matches = createdDimension.items.filter((it) => it.name === itemName);
    if (matches.length === 0) return null;
    return Math.max(...matches.map((it) => it.id));
  };

  const getCurrentAssigneeId = (node) => {
    if (node.defaultAssigneeId != null) return Number(node.defaultAssigneeId);
    return null;
  };

  const applyAssigneeToDescendants = async (targetUserId) => {
    await Promise.all(descendants.map((d) => setDefaultAssigneeAsync({ id: d.id, userId: targetUserId })));
  };

  const finalizeSave = ({ closeAfter = true } = {}) => {
    queryClient.invalidateQueries(dimensionAdminKeys.tree());
    if (closeAfter) {
      onSuccess();
      onClose();
    } else {
      onSuccess();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;

    // Belt-and-braces: ORG_ADMIN should not reach add-mode (route + tree buttons hidden).
    if (!isESGAdmin && !isEditing) return;

    const targetUserId = defaultAssigneeId === "" ? null : Number(defaultAssigneeId);

    try {
      if (isEditing) {
        if (name.trim() !== item.name) {
          await renameItemAsync({ id: item.id, name: name.trim() });
        }
        const previous = getCurrentAssigneeId(item);
        if (targetUserId !== previous) {
          await setDefaultAssigneeAsync({ id: item.id, userId: targetUserId });
        }

        // Cascade to descendants if requested. Detect existing assignments
        // that would be overwritten and confirm before clobbering them.
        if (applyToDescendants && hasDescendants) {
          const conflicts = descendants.filter((d) => {
            const cur = getCurrentAssigneeId(d);
            return cur != null && cur !== targetUserId;
          });
          if (conflicts.length > 0) {
            // Pause here — confirmation dialog will resume the cascade.
            setConfirmState({ open: true, conflicts, targetUserId });
            return;
          }
          await applyAssigneeToDescendants(targetUserId);
        }

        showSuccessToast(toast, "Item updated.");
      } else if (isAddingRoot) {
        const created = await addRootItemAsync({
          dimensionId: rootDimension.id,
          data: { name: name.trim() },
        });
        const newId = findNewItemId(created, name.trim());
        if (newId && targetUserId != null) {
          await setDefaultAssigneeAsync({ id: newId, userId: targetUserId });
        }
        showSuccessToast(toast, "Item created.");
      } else if (parentItem) {
        const created = await addChildItemAsync({
          parentItemId: parentItem.id,
          data: { name: name.trim() },
        });
        const newId = findNewItemId(created, name.trim());
        if (newId && targetUserId != null) {
          await setDefaultAssigneeAsync({ id: newId, userId: targetUserId });
        }
        showSuccessToast(toast, "Item created.");
      }
      finalizeSave();
    } catch {
      // toast already shown by the mutation onError
    }
  };

  const handleConfirmOverwrite = async () => {
    try {
      await applyAssigneeToDescendants(confirmState.targetUserId);
      showSuccessToast(toast, "Item updated.");
    } finally {
      setConfirmState({ open: false, conflicts: [] });
      finalizeSave();
    }
  };

  const handleCancelOverwrite = () => {
    // Save the parent's change but skip cascading.
    setConfirmState({ open: false, conflicts: [] });
    showSuccessToast(toast, "Item updated. Existing child assignees were not changed.");
    finalizeSave();
  };

  let modalTitle;
  let helpText;
  if (isEditing) {
    modalTitle = isESGAdmin ? "Edit Item" : "Default assignee";
    helpText = null;
  } else if (isAddingRoot) {
    modalTitle = `Add ${rootDimension.name}`;
    helpText = `This item will be added to the ${rootDimension.name} dimension.`;
  } else if (parentItem && childDimensionName) {
    modalTitle = `Add ${childDimensionName} under "${parentItem.name}"`;
    helpText = `This item will be created in the ${childDimensionName} dimension.`;
  } else if (parentItem) {
    modalTitle = `Add child under "${parentItem.name}"`;
    helpText = null;
  } else {
    modalTitle = "Add Item";
    helpText = null;
  }

  const isSubmitting = isAddingChild || isAddingRootItem || isRenaming || isSettingAssignee;

  return (
    <>
    <Modal isOpen={isOpen} onClose={onClose} size="md">
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit}>
          <ModalHeader>{modalTitle}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing={4}>
              {helpText && (
                <Text fontSize="sm" color="gray.600">
                  {helpText}
                </Text>
              )}
              <FormControl isRequired>
                <FormLabel>Name</FormLabel>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Melbourne, Building A"
                  autoFocus={isESGAdmin}
                  isReadOnly={!isESGAdmin}
                  _readOnly={{ bg: "gray.50", cursor: "not-allowed" }}
                  borderColor="gray.300"
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
                />
              </FormControl>
              <FormControl>
                <FormLabel>Default assignee</FormLabel>
                <Select
                  value={defaultAssigneeId}
                  onChange={(e) => setDefaultAssigneeId(e.target.value)}
                  placeholder={isUsersLoading ? "Loading users..." : "No default assignee"}
                  isDisabled={isUsersLoading}
                  borderColor="gray.300"
                  _hover={{ borderColor: "gray.400" }}
                  _focus={{ borderColor: "lime", boxShadow: "0 0 0 1px var(--chakra-colors-lime)" }}
                >
                  {sortUsersByEmail(users).map((user) => (
                    <option key={user.id} value={String(user.id)}>
                      {user.email}
                    </option>
                  ))}
                </Select>
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Used when creating tasks for this dimension item without picking an assignee.
                </Text>
              </FormControl>

              {isEditing && hasDescendants && isESGAdmin && (
                <Checkbox
                  isChecked={applyToDescendants}
                  onChange={(e) => setApplyToDescendants(e.target.checked)}
                  colorScheme="limeShade"
                >
                  <Text fontSize="sm">
                    Apply to all child items ({descendants.length})
                  </Text>
                </Checkbox>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter gap={2}>
            <Button variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button variant="primary" type="submit" isLoading={isSubmitting} isDisabled={!name.trim()}>
              {isEditing ? (isESGAdmin ? "Save" : "Save assignee") : "Create"}
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>

    <AlertDialog
      isOpen={confirmState.open}
      onClose={handleCancelOverwrite}
      leastDestructiveRef={cancelConfirmRef}
      isCentered
    >
      <AlertDialogOverlay>
        <AlertDialogContent>
          <AlertDialogHeader fontSize="lg" fontWeight="bold">
            Overwrite existing assignees?
          </AlertDialogHeader>
          <AlertDialogBody>
            <Text mb={3}>
              {confirmState.conflicts.length} child item{confirmState.conflicts.length === 1 ? "" : "s"} already
              {confirmState.conflicts.length === 1 ? " has" : " have"} a different default assignee. Do you want to
              overwrite {confirmState.conflicts.length === 1 ? "it" : "them"}?
            </Text>
            <Stack spacing={1} maxH="200px" overflowY="auto" fontSize="sm" color="gray.600">
              {confirmState.conflicts.slice(0, 20).map((c) => (
                <Text key={c.id}>
                  <Text as="span" fontWeight="600">{c.name}</Text> — {c.defaultAssigneeEmail || "existing assignee"}
                </Text>
              ))}
              {confirmState.conflicts.length > 20 && (
                <Text fontStyle="italic">
                  …and {confirmState.conflicts.length - 20} more.
                </Text>
              )}
            </Stack>
          </AlertDialogBody>
          <AlertDialogFooter gap={2}>
            <Button ref={cancelConfirmRef} variant="ghost" onClick={handleCancelOverwrite}>
              Keep existing
            </Button>
            <Button colorScheme="red" onClick={handleConfirmOverwrite}>
              Overwrite
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
    </>
  );
}

export default EditDimensionItemModal;
